/**
 * PI-QUAL scoring logic — pure function, no React dependencies.
 *
 * Reference: https://link.springer.com/article/10.1007/s00330-024-10795-4
 *
 * PI-QUAL score of 1: the image quality is inadequate (T2-WI and/or DWI score ≤ 2/4).
 * PI-QUAL score of 2: the image quality is acceptable (T2-WI and DWI score at least 3/4).
 * PI-QUAL score of 3: the image quality is optimal (T2-WI and DWI both score 4/4 and both DCE criteria satisfied).
 *
 * Special upgrade case (with DCE): if T2-WI or DWI score ≤ 2/4 but the other scores 4/4
 * AND both DCE criteria are met, the score is upgraded from 1 → 2.
 */

export interface PIQUALScore {
  axial_t2_snr?: boolean
  axial_t2_delineate?: boolean
  axial_t2_artifacts?: boolean
  sag_coronal_t2_snr_and_artifacts?: boolean
  dwi_contrast?: boolean
  dwi_adc?: boolean
  dwi_artifacts?: boolean
  dwi_matching_adc_t2?: boolean
  has_dce?: boolean
  dce_artifacts?: boolean
  dce_structures?: boolean

  /** Sum of T2-WI criteria (0-4) */
  t2_wi_score?: number
  /** Sum of DWI criteria (0-4) */
  dwi_score?: number
  /** Number of DCE criteria met (0-2) */
  dce_criteria_met?: number
  /** Final PI-QUAL score: 1=inadequate, 2=acceptable, 3=optimal */
  score?: number
}

export function calculatePIQUALScore(answers: PIQUALScore): PIQUALScore {
  // Calculate T2-WI score (out of 4)
  const t2Answers = [
    answers.axial_t2_snr,
    answers.axial_t2_delineate,
    answers.axial_t2_artifacts,
    answers.sag_coronal_t2_snr_and_artifacts
  ]
  const t2_wi_score = t2Answers.filter((a) => a === true).length

  // Calculate DWI score (out of 4)
  const dwiAnswers = [answers.dwi_contrast, answers.dwi_adc, answers.dwi_artifacts, answers.dwi_matching_adc_t2]
  const dwi_score = dwiAnswers.filter((a) => a === true).length

  // Calculate DCE criteria met (out of 2)
  const dce_criteria_met = [answers.dce_artifacts, answers.dce_structures].filter((a) => a === true).length

  let score: number

  if (answers.has_dce) {
    // PI-QUAL 3 (optimal): T2-WI and DWI both 4/4 AND both DCE criteria satisfied
    if (t2_wi_score === 4 && dwi_score === 4 && dce_criteria_met === 2) {
      score = 3
    }
    // PI-QUAL 1 (inadequate): T2-WI and/or DWI ≤ 2/4
    else if (t2_wi_score <= 2 || dwi_score <= 2) {
      // Special upgrade: both DCE criteria met AND at least one sequence scores 4/4
      if (dce_criteria_met === 2 && (t2_wi_score === 4 || dwi_score === 4)) {
        score = 2
      } else {
        score = 1
      }
    }
    // PI-QUAL 2 (acceptable): T2-WI and DWI both ≥ 3/4
    else if (t2_wi_score >= 3 && dwi_score >= 3) {
      score = 2
    }
    // Default fallback
    else {
      score = 1
    }
  } else {
    // Without DCE
    if (t2_wi_score === 4 && dwi_score === 4) {
      score = 3
    } else if (t2_wi_score <= 2 || dwi_score <= 2) {
      score = 1
    } else if (t2_wi_score >= 3 && dwi_score >= 3) {
      score = 2
    } else {
      score = 1
    }
  }

  return {
    ...answers,
    t2_wi_score,
    dwi_score,
    dce_criteria_met,
    score
  }
}
